import React, { useState, useEffect } from 'react';
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import { Link } from 'react-router-dom';

function UserList() {
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState([]);
    const [page, setPage] = useState(1);
    const [itemsPerPage] = useState(6);
    const [totalItems, setTotalItems] = useState(0);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('aprendices'); // Estado para seguir el filtro activo
    const [searchTerm, setSearchTerm] = useState(''); // Estado para el término de búsqueda

    // Función genérica para cargar datos según el tipo de usuario
    const fetchUsersByType = async (userType) => {
        try {
            setLoading(true);
            let endpoint = '';

            // Seleccionar el endpoint según el tipo de usuario
            switch(userType) {
                case 'aprendices':
                    endpoint = `${API_URL}/v1/aprendices-all`;
                    break;
                case 'vigilantes':
                    endpoint = `${API_URL}/v1/vigilantes-all`;
                    break;
                case 'admins':
                    endpoint = `${API_URL}/v1/admins-all`;
                    break;
                default:
                    endpoint = `${API_URL}/v1/aprendices-all`;
            }

            const response = await authenticatedFetch(endpoint, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error al cargar la lista de ${userType}`);
            }

            const data = await response.json();
            setUserData(data);
            setTotalItems(data.length);
            setActiveFilter(userType);
            setPage(1); // Resetear a la primera página al cambiar el filtro
        } catch (error) {
            console.error('Error:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Cargar aprendices por defecto al iniciar
        fetchUsersByType('aprendices');
    }, []);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Filtrar usuarios según el término de búsqueda
    const filteredUsers = userData.filter(user => {
        if (!searchTerm) return true; // Si no hay término de búsqueda, mostrar todos

        // Buscar por nombre (nombre completo)
        const fullName = `${user.name} ${user.last_name}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    });

    // Actualizar el total de items después de filtrar
    useEffect(() => {
        setTotalItems(filteredUsers.length);
        // Si después de filtrar no hay resultados en la página actual, volver a la primera página
        if (filteredUsers.length > 0 && (page - 1) * itemsPerPage >= filteredUsers.length) {
            setPage(1);
        }
    }, [filteredUsers.length, page, itemsPerPage]);

    // Obtener los usuarios de la página actual después de filtrar
    const currentUsers = filteredUsers.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    // Función para obtener el color del estado
    const getStatusColor = (status) => {
      switch (status) {
          case 1:                
              return 'bg-yellow-100 text-yellow-800';
          case 2:
              return 'bg-green-300 text-green-900';
          case 3:
              return 'bg-red-100 text-red-800';
          case 4:
              return 'bg-yellow-300 text-orange-800';
          case 5:
              return 'bg-green-100 text-green-600';
          case 6:
              return 'bg-red-200 text-pink-900';
          default:
              return 'bg-yellow-100 text-yellow-800';
      }
  };

    // Función para obtener el texto del estado
    const getStatusText = (status) => {
        switch (status) {
            case 1:
                return 'Pendiente';
            case 2:
                return 'Activo';
            case 3:
              return 'Inactivo';
            case 4:
                return 'Recibido';
            case 5:
                return 'Aceptado';
            case 6:
                return 'Rechazado';
            default:
                return 'Pendiente';
        }
    };

    // Función para el estilo de los botones de filtro
    const getFilterButtonStyles = (filterType) => {
        const baseStyles = "px-4 py-2 text-sm  mt-6 font-medium rounded-lg shadow-sm focus:outline-none transition-colors duration-200";

        if (activeFilter === filterType) {
            return `${baseStyles} bg-amber-600 text-white`;
        }
        return `${baseStyles} bg-gray-100 text-gray-700 hover:bg-gray-200`;
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="user-list-container">
            {/* Filtros de tipo de usuario */}
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 my-4" data-testid="user-type-filters">
                <button 
                    onClick={() => fetchUsersByType('aprendices')}
                    className={getFilterButtonStyles('aprendices')}
                    data-testid="filter-aprendices-button"
                >
                    Aprendices
                </button>
                <button 
                    onClick={() => fetchUsersByType('vigilantes')}
                    className={getFilterButtonStyles('vigilantes')}
                    data-testid="filter-vigilantes-button"
                >
                    Vigilantes
                </button>
                <button 
                    onClick={() => fetchUsersByType('admins')}
                    className={getFilterButtonStyles('admins')}
                    data-testid="filter-admins-button"
                >
                    Administradores
                </button>
            </div>

            {/* Buscador por nombre */}
            <div className="mt-6 mb-4" data-testid="user-search-container">
                <div className="flex">
                    <div className="flex-1">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1); // Resetear a la primera página al buscar
                                }}
                                placeholder="Buscar por nombre..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-colors duration-200"
                                data-testid="user-search-input"
                            />
                            <button
                                onClick={() => setSearchTerm('')}
                                className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-purple-500 hover:text-purple-500 ${!searchTerm && 'hidden'}`}
                                data-testid="user-search-clear-button"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center p-4" data-testid="user-list-loading">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-700"></div>
                    <span className="ml-2">Cargando usuarios...</span>
                </div>
            )}

            {!loading && (
                <>
                    <div className="overflow-x-auto shadow-md rounded-lg mt-6" data-testid="user-list-wrapper">
                        <table className="min-w-full divide-y divide-gray-300 rounded-lg overflow-hidden" data-testid="user-list-table">
                            <thead className="bg-gray-50" data-testid="user-list-header">
                                <tr>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="user-list-header-id">ID</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="user-list-header-name">Nombre</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell" data-testid="user-list-header-email">Email</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="user-list-header-status">Estado</th>
                                    <th scope="col" className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-testid="user-list-header-actions">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-300" data-testid="user-list-body">
                                {currentUsers.length > 0 ? (
                                    currentUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50" data-testid={`user-list-row-${user.id}`}>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`user-list-cell-id-${user.id}`}>{user.id}</td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`user-list-cell-name-${user.id}`}>{user.name} {user.last_name}</td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 hidden md:table-cell" data-testid={`user-list-cell-email-${user.id}`}>{user.email}</td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap" data-testid={`user-list-cell-status-${user.id}`}>
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.status_id)}`}>
                                                    {getStatusText(user.status_id)}
                                                </span>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900" data-testid={`user-list-cell-actions-${user.id}`}>
                                                <Link to={`/user-detail/id/${user.id}?userType=${activeFilter}`} className="text-amber-700 hover:text-amber-900 font-medium" data-testid={`user-list-view-link-${user.id}`}>
                                                    Ver
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-3 sm:px-6 py-4 text-center text-sm text-gray-500">
                                            {searchTerm ? (
                                                <div className="py-2">
                                                    <p className="font-medium">No se encontraron usuarios que coincidan con "{searchTerm}".</p>
                                                    <p className="text-xs mt-1">Intenta con otro término de búsqueda.</p>
                                                </div>
                                            ) : (
                                                "No se encontraron usuarios con este filtro."
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {currentUsers.length > 0 && (
                        <div className="mt-4 mb-4 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="user-list-pagination">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="w-full sm:w-auto px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="user-list-prev-button"
                            >
                                <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                                <div className={`absolute inset-0 ${page === 1 ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                                <span className="z-10 text-gray-900">Anterior</span>
                            </button>

                            <span className="mx-2 py-1 text-sm text-center" data-testid="user-list-page-indicator">
                                Página {page} de {Math.ceil(totalItems / itemsPerPage)}
                            </span>

                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= Math.ceil(totalItems / itemsPerPage)}
                                className="w-full sm:w-auto px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="user-list-next-button"
                            >
                                <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / itemsPerPage) ? 'bg-gray-400' : 'bg-gradient-to-t from-amber-300 to-amber-700'} transition-transform duration-700 ease-in-out`}></div>
                                <div className={`absolute inset-0 ${page >= Math.ceil(totalItems / itemsPerPage) ? 'bg-gray-400' : 'bg-gradient-to-b from-amber-300 to-amber-700'} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out`}></div>
                                <span className="z-10 text-gray-900">Siguiente</span>
                            </button>
                        </div>
                    )}
                </>
            )}

            {error && (
                <div className="text-center text-red-600 p-4" data-testid="user-list-error">
                    Error al cargar los usuarios: {error}
                </div>
            )}
        </div>
    );
}

export { UserList };
